// src/features/locations/PeopleTab.jsx
//
// PEOPLE-S2 — "People" tab on LocationDetail.
// Fetches GET /api/people/profiles/:locId and renders five sections:
//   1. Population tiles (4 stat tiles)
//   2. Function mix horizontal bar chart
//   3. M&A cohort horizontal bar chart
//   4. Commute bands bar chart + SYNTHETIC badge when provenance='synthetic'
//   5. Deferred attendance-by-segment placeholder (dashed card)
//
// States handled before rendering metric panels:
//   - Loading skeleton
//   - Error banner
//   - No-profile (404 → null) — neutral empty state
//   - Suppressed (headcountTotal < 10) — floor message
//
// All colors via T design tokens; no raw hex in this file.

import React, { useEffect, useState } from "react";
import { T } from "../../app/tokens.js";
import { S } from "../../app/styles.js";
import { getPeopleProfile } from "../../data/peopleClient.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { log } from "../../utils/logger.js";

// ─── Constants ────────────────────────────────────────────────────────────────

// k-anonymity floor (must match server/lib/peopleProfile.js meetsKThreshold)
const K_FLOOR = 10;

// Bar chart max-width for a 100% value (px)
const BAR_MAX_WIDTH = 240;

// ─── Pure helpers (exported for unit tests) ───────────────────────────────────

/**
 * Returns true when the profile should be suppressed from display.
 * The server does not suppress at the API layer in S1/S2 — suppression
 * is a client-side presentation gate (headcountTotal below k-floor).
 *
 * @param {object} profile  camelCase profile from the API
 * @param {number} [k=10]
 * @returns {boolean}
 */
export function isSuppressed(profile, k = K_FLOOR) {
  return profile?.suppressed === true || (profile?.headcountTotal ?? 0) < k;
}

/**
 * Sort an array of function-mix entries descending by pct.
 * Returns a new array; does not mutate input.
 *
 * @param {Array<{function: string, pct: number}>} items
 * @returns {Array<{function: string, pct: number}>}
 */
export function sortFunctionMix(items) {
  if (!Array.isArray(items)) return [];
  return [...items].sort((a, b) => b.pct - a.pct);
}

/**
 * Derive ordered commute band entries from the commute.bands JSONB object.
 * Returns entries in display order: short → long commutes.
 *
 * @param {object} bands  e.g. { "0-15": 17, "15-30": 20, "30-45": 37, "45-60": 10, "60+": 17 }
 * @returns {Array<{label: string, pct: number}>}
 */
export function commuteBandEntries(bands) {
  if (!bands || typeof bands !== "object") return [];
  const ORDER = ["0-15", "15-30", "30-45", "45-60", "60+"];
  return ORDER
    .filter((k) => k in bands)
    .map((k) => ({ label: k, pct: bands[k] }));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionTitle({ children }) {
  return (
    <h3 style={{ ...S.sectionTitle, marginTop: 0, marginBottom: 10 }}>
      {children}
    </h3>
  );
}

function StatTile({ label, value, sub }) {
  return (
    <div
      style={{
        background: T.kpiTileBg,
        borderRadius: 8,
        padding: "12px 14px",
        minWidth: 0,
        flex: "1 1 0",
      }}
    >
      <div style={{ ...S.statLabel, color: T.mid, marginBottom: 4 }}>{label}</div>
      <div style={{ ...S.statValue, color: T.dark }}>{value ?? "—"}</div>
      {sub && (
        <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3 }}>{sub}</div>
      )}
    </div>
  );
}

function HorizBar({ label, pct, barColor, bgColor, maxWidth = BAR_MAX_WIDTH, valueLabel }) {
  const fillWidth = Math.round((pct / 100) * maxWidth);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <div style={{ width: 160, fontSize: T.fsSm, color: T.dark, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </div>
      <div style={{ width: maxWidth, background: bgColor, borderRadius: 3, overflow: "hidden", flexShrink: 0 }}>
        <div style={{ width: fillWidth, height: 14, background: barColor, borderRadius: 3 }} />
      </div>
      <div style={{ fontSize: T.fsXs, color: T.mid, flexShrink: 0 }}>
        {valueLabel !== undefined ? valueLabel : `${pct}%`}
      </div>
    </div>
  );
}

function PanelCard({ children, style }) {
  return (
    <div
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        boxShadow: T.cardShadow,
        padding: "14px 16px",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * People tab content. Fetches its own profile on mount (same pattern as
 * LeaseSummaryTab), so LocationDetail only passes the location ID.
 *
 * @param {string} locationId  canonical location ID
 */
export function PeopleTab({ locationId }) {
  const [profile, setProfile] = useState(undefined); // undefined=loading, null=no-profile, object=loaded
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setProfile(undefined);
    setFetchError(null);
    getPeopleProfile(locationId)
      .then((data) => {
        if (cancelled) return;
        setProfile(data); // null = 404 = no profile
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("[PeopleTab] fetch failed", { id: locationId, name: err?.name, message: err?.message });
        setFetchError(err);
      });
    return () => { cancelled = true; };
  }, [locationId]);

  // ── Loading ──
  if (profile === undefined && !fetchError) {
    return (
      <div data-testid="people-tab-loading" style={{ padding: 24, color: T.light, fontSize: T.fsBase }}>
        Loading people data…
      </div>
    );
  }

  // ── Error ──
  if (fetchError) {
    return (
      <div data-testid="people-tab-error" style={{ padding: 8 }}>
        <ErrorBanner error={fetchError} />
      </div>
    );
  }

  // ── No-profile state (404) ──
  if (profile === null) {
    return (
      <div data-testid="people-tab-empty" style={{ padding: 24, color: T.mid, fontSize: T.fsBase }}>
        No people profile for this location.
      </div>
    );
  }

  // ── Suppressed state ──
  if (isSuppressed(profile)) {
    return (
      <div data-testid="people-tab-suppressed" style={{ padding: 24, fontSize: T.fsBase, color: T.mid }}>
        Too few to report - {profile.headcountTotal ?? 0} assigned (floor is {K_FLOOR})
      </div>
    );
  }

  // ── Full profile ──
  const {
    headcountTotal,
    employeeCount,
    contingentCount,
    newHires90d,
    medianTenureYears,
    maTotal,
    maCohorts,
    functionMix,
    commute,
    commuteProvenance,
  } = profile;

  // Section 3 — M&A cohort: find acquisition count (ma_total = maTotal)
  const acquisitionCount = Array.isArray(maCohorts) ? maCohorts.length : 0;

  // Section 4 — Commute
  const bands     = commute?.bands ?? {};
  const medianMin = commute?.medianMin ?? null;
  const pctOver45 = commute?.pctOver45 ?? null;
  const isSynthetic = commuteProvenance === "synthetic";

  const sortedFunctions = sortFunctionMix(functionMix);
  const commuteBands    = commuteBandEntries(bands);

  // Compute total for commute bars (bands are percentages; sum may not be 100 if data is sparse)
  const commuteTotal = commuteBands.reduce((s, b) => s + b.pct, 0) || 100;

  return (
    <div data-testid="people-tab" style={{ display: "flex", flexDirection: "column", gap: 12 }}>

      {/* ── Section 1: Population tiles ──────────────────────────────── */}
      <PanelCard>
        <SectionTitle>Population</SectionTitle>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <StatTile
            label="Assigned"
            value={headcountTotal}
            sub={`${employeeCount ?? "—"} emp · ${contingentCount ?? "—"} contingent`}
          />
          <StatTile
            label="New hires 90d"
            value={newHires90d}
          />
          <StatTile
            label="Median tenure"
            value={medianTenureYears != null ? `${medianTenureYears}y` : "—"}
          />
          <StatTile
            label="M&A origin"
            value={maTotal}
            sub={`${acquisitionCount} acquisition${acquisitionCount !== 1 ? "s" : ""}`}
          />
        </div>
      </PanelCard>

      {/* ── Sections 2 + 3: Two-column grid (Function mix | M&A cohort) ── */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>

        {/* Section 2 — Function mix */}
        <PanelCard>
          <SectionTitle>Function mix</SectionTitle>
          {sortedFunctions.length === 0 ? (
            <div style={{ color: T.light, fontSize: T.fsSm }}>No function data.</div>
          ) : (
            sortedFunctions.map((item) => (
              <HorizBar
                key={item.function}
                label={item.function}
                pct={item.pct}
                barColor={T.purple}
                bgColor={T.purpleBg}
              />
            ))
          )}
        </PanelCard>

        {/* Section 3 — M&A cohort */}
        <PanelCard>
          <SectionTitle>M&A cohort</SectionTitle>
          {!Array.isArray(maCohorts) || maCohorts.length === 0 ? (
            <div style={{ color: T.light, fontSize: T.fsSm }}>No M&A cohort data.</div>
          ) : (
            (() => {
              const maxCount = Math.max(...maCohorts.map((c) => c.count));
              return maCohorts.map((cohort) => {
                const pct = maxCount > 0 ? Math.round((cohort.count / maxCount) * 100) : 0;
                return (
                  <HorizBar
                    key={cohort.company}
                    label={cohort.company}
                    pct={pct}
                    barColor={T.statPink}
                    bgColor={T.purpleBg}
                    valueLabel={cohort.count}
                  />
                );
              });
            })()
          )}
        </PanelCard>
      </div>

      {/* ── Section 4: Commute ──────────────────────────────────────────── */}
      <PanelCard>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <SectionTitle>Commute</SectionTitle>
          {isSynthetic && (
            <span
              data-testid="commute-synthetic-badge"
              style={{
                fontSize: T.fsXxs,
                fontWeight: T.fwBold,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                color: T.amberDark,
                background: T.warnAmberBg,
                border: `1px solid ${T.decisionsCardBorder}`,
                borderRadius: 4,
                padding: "2px 6px",
                marginTop: -2,
              }}
            >
              SYNTHETIC
            </span>
          )}
        </div>
        {medianMin != null || pctOver45 != null ? (
          <div style={{ fontSize: T.fsSm, color: T.mid, marginBottom: 10 }}>
            Median {medianMin != null ? `${medianMin} min` : "—"} · {pctOver45 != null ? `${pctOver45}%` : "—"} over 45 min
          </div>
        ) : null}
        {commuteBands.length === 0 ? (
          <div style={{ color: T.light, fontSize: T.fsSm }}>No commute data.</div>
        ) : (
          commuteBands.map((band) => (
            <HorizBar
              key={band.label}
              label={`${band.label} min`}
              pct={Math.round((band.pct / commuteTotal) * 100)}
              barColor={T.statCyan}
              bgColor={T.blueSoft}
            />
          ))
        )}
      </PanelCard>

      {/* ── Section 5: Deferred attendance placeholder ──────────────────── */}
      <div
        data-testid="attendance-deferred-card"
        style={{
          border: `2px dashed ${T.border}`,
          borderRadius: 8,
          padding: "16px 20px",
          color: T.mid,
          fontSize: T.fsSm5,
        }}
      >
        <span style={{ fontWeight: T.fwSemibold, color: T.dark }}>Attendance by segment</span>
        {" - "}coming when the badge × Workday-segment join lands.
      </div>
    </div>
  );
}
